/**
 * 过大图片在上传前压缩：限制长边，输出 JPEG。
 * 小图 / 小体积 GIF 原样返回；无法解码时退回原文件。
 */
export async function compressImageFile(
  file,
  {
    maxEdge = 1920,
    maxBytes = 2.5 * 1024 * 1024,
    quality = 0.82,
  } = {}
) {
  if (!file || !(file instanceof Blob)) return file;
  const type = (file.type || "").toLowerCase();
  if (type && !type.startsWith("image/")) return file;
  if (type === "image/gif" && file.size <= maxBytes) return file;
  if (
    file.size <= 1.2 * 1024 * 1024 &&
    type !== "image/heic" &&
    type !== "image/heif"
  ) {
    return file;
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const srcW = bitmap.width;
  const srcH = bitmap.height;
  if (!srcW || !srcH) {
    bitmap.close?.();
    return file;
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close?.();
    return file;
  }

  const drawScaled = (edge) => {
    const scale = Math.min(1, edge / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(bitmap, 0, 0, w, h);
  };

  const toBlob = (q) =>
    new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", q);
    });

  let edge = maxEdge;
  let q = quality;
  drawScaled(edge);
  let blob = await toBlob(q);

  while (blob && blob.size > maxBytes && q > 0.45) {
    q = Math.round((q - 0.1) * 100) / 100;
    blob = await toBlob(q);
  }
  while (blob && blob.size > maxBytes && edge > 960) {
    edge = Math.round(edge * 0.75);
    drawScaled(edge);
    blob = await toBlob(Math.max(0.5, q));
  }

  bitmap.close?.();
  if (!blob) return file;

  const name = (file.name || "photo.jpg").replace(/\.\w+$/i, ".jpg");
  return new File([blob], name, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}
